<template>
  <!-- Assessments of a process and its activities in one tab (US 9369 + 9371): what covers each activity, and the
       assessments with what they cover. An assessment of the process covers all its activities. -->
  <div v-if="data" class="pa-tab" qid="process-assessments">
    <div v-if="pickedId" class="rk-showing" qid="assessments-showing">
      <span class="rk-showing__lab">{{ $t('risk_tab.showing.label') }}</span>
      <div class="v2-seg" role="tablist">
        <span role="tab" :class="{ on: showing === 'picked' }" :aria-selected="showing === 'picked' ? 'true' : 'false'" @click="showing = 'picked'">
          {{ $t('risk_tab.showing.picked', { name: pickedName }) }} <i>{{ countFor('picked') }}</i>
        </span>
        <span role="tab" :class="{ on: showing === 'all' }" :aria-selected="showing === 'all' ? 'true' : 'false'" @click="showing = 'all'">
          {{ $t('risk_tab.showing.all') }} <i>{{ countFor('all') }}</i>
        </span>
      </div>
    </div>

    <section class="v2-card sp-sec">
      <div class="sp-sec__head">
        <font-awesome-icon icon="th-large"/>
        <h3>{{ $t('process_assessments.covered') }}</h3>
        <small class="sp-sub">{{ $t('process_assessments.covered_hint') }}</small>
      </div>
      <div class="pa-cov" :style="{ gridTemplateColumns: 'minmax(180px, 1.2fr) repeat(' + data.types.length + ', minmax(0, 1fr))' }">
        <div class="pa-cov__hd">{{ $t('process_assessments.what') }}</div>
        <div v-for="type in data.types" :key="'h' + type" class="pa-cov__hd">{{ $t('process_assessments.types.' + type) }}</div>
        <template v-for="row in data.coverage">
          <div :key="'n' + row.kind + row.id" class="pa-cov__name" :class="{ 'is-process': row.kind === 'process', on: row.kind === 'activity' && row.id === pickedId }">
            <font-awesome-icon :icon="row.kind === 'process' ? 'sitemap' : 'cube'"/>{{ row.name }}
            <small v-if="row.kind === 'process'">{{ $t('process_assessments.process') }}</small>
          </div>
          <div v-for="type in data.types"
               :key="row.kind + row.id + type"
               class="pa-cov__cell"
               :class="[cellClass(row.cells[type]), { 'is-process': row.kind === 'process' }]">
            <template v-if="row.cells[type]">
              <router-link :to="{ name: 'projects.view', params: { slug: row.cells[type].project.slug } }">
                <font-awesome-icon :icon="row.cells[type].via === 'process' ? 'sitemap' : (row.cells[type].project.finished ? 'check' : 'clock')"/>
                {{ row.cells[type].via === 'process' ? $t('process_assessments.through_process') : row.cells[type].project.title }}
              </router-link>
              <small>{{ row.cells[type].via === 'process' ? row.cells[type].project.title : cellNote(row.cells[type].project) }}</small>
            </template>
            <span v-else class="pa-cov__none">–</span>
          </div>
        </template>
      </div>
    </section>

    <section class="v2-card sp-sec mt-3">
      <div class="sp-sec__head">
        <font-awesome-icon icon="wave-square"/>
        <h3>{{ $t('process_assessments.title') }}</h3>
        <span class="sp-grow"></span>
        <b-button v-if="canStart" size="sm" variant="success" qid="process-assessment-start" @click="$emit('start')">
          <font-awesome-icon icon="plus"/> {{ $t('process_assessments.start') }}
        </b-button>
      </div>
      <div v-if="shown.length" class="pa-row pa-row--head">
        <div>{{ $t('process_assessments.assessment') }}</div>
        <div>{{ $t('process_assessments.type') }}</div>
        <div>{{ $t('process_assessments.covers') }}</div>
        <div>{{ $t('process_assessments.status') }}</div>
      </div>
      <router-link v-for="item in shown"
                   :key="item.id"
                   :to="{ name: 'projects.view', params: { slug: item.slug } }"
                   class="pa-row"
                   :qid="'process-assessment-' + item.id">
        <div>
          <b>{{ item.title }}</b>
          <small>{{ [item.template, formatDate(item.date)].filter(part => part).join(' · ') }}</small>
        </div>
        <div><span class="pa-type">{{ $t('process_assessments.types.' + item.type) }}</span></div>
        <div class="pa-row__covers">
          <span v-for="cover in item.covers" :key="cover.kind + cover.id" class="rk-applies" :class="cover.kind === 'process' ? 'is-process' : 'is-activity'">
            <font-awesome-icon :icon="cover.kind === 'process' ? 'sitemap' : 'cube'"/>{{ cover.kind === 'process' ? $t('risk_tab.applies_all', { name: cover.name }) : cover.name }}
          </span>
        </div>
        <div>
          <span class="sx-st" :class="item.finished ? 'ok' : 'part'">
            <font-awesome-icon :icon="item.finished ? 'check' : 'clock'"/>{{ item.finished ? $t('process_assessments.finished') : $t('process_assessments.in_progress') }}
          </span>
        </div>
      </router-link>
      <p v-if="!shown.length" class="sx-empty">{{ $t('process_assessments.empty') }}</p>
    </section>
  </div>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';

  export default {
    name: 'ProcessAssessmentsTab',
    props: {
      process: { type: Object, required: true },
      // The activity picked on the page, if any
      pickedId: { type: Number, default: null },
      canStart: { type: Boolean, default: false }
    },
    data() {
      return {
        data: null,
        showing: 'picked'
      };
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      pickedName() {
        let row = this.data ? this.data.coverage.find(item => item.kind === 'activity' && item.id === this.pickedId) : null;
        return row ? row.name : '';
      },
      shown() {
        if (!this.pickedId || this.showing === 'all') {
          return this.data.assessments;
        }
        return this.data.assessments.filter(this.coversPicked);
      }
    },
    watch: {
      process(value, old) {
        if (!old || value.id !== old.id) {
          this.load();
        }
      }
    },
    created() {
      this.load();
    },
    methods: {
      load() {
        let self = this;
        piincHttp.get('processes/' + this.process.slug + '/assessments-overview', { params: { client: this.getClient.slug } }).then(function(response) {
          self.data = response.data;
        }, function() {});
      },
      // The picked activity's own assessments and the process's, which cover it
      coversPicked(item) {
        return item.covers.some(cover => cover.kind === 'process' || cover.id === this.pickedId);
      },
      countFor(mode) {
        return mode === 'picked' ? this.data.assessments.filter(this.coversPicked).length : this.data.assessments.length;
      },
      cellClass(cell) {
        if (!cell) {
          return 'is-none';
        }
        if (cell.via === 'process') {
          return 'is-via';
        }
        return cell.project.finished ? 'is-ok' : 'is-due';
      },
      cellNote(project) {
        return project.finished ? this.formatDate(project.date) : this.$t('process_assessments.in_progress');
      },
      formatDate(value) {
        return value ? moment(value).format('D MMM YYYY') : '';
      }
    }
  };
</script>
